import { Injectable } from '@angular/core';

/**
 * Enter in a dialog field moves the user forward — to the next required
 * field that is still empty, or, when none is left, to the dialog's primary
 * action ("Weiter", "Erstellen", "Speichern") — without every dialog wiring
 * it up itself.
 *
 * Browsers only submit natively for a `<form>` that has a submit button
 * (implicit submission), and even then they never jump between fields. Most
 * portal dialogs drive their primary button via `(click)`, so Enter was dead
 * there. One document-level keydown listener closes the gap:
 *
 * - Plain Enter in a text-like `<input>`; in a `<textarea>` only Ctrl/Cmd+Enter
 *   (plain Enter stays a line break). Never in search fields
 *   (`app-search-input`), never on a control marked `data-enter-ignore`,
 *   never during IME composition.
 * - Only inside an `nb-dialog-container`. Page forms keep their own rules.
 * - Not while an overlay owns Enter (open `nb-select` option list,
 *   autocomplete, datepicker), and not if something already
 *   `preventDefault()`ed the keydown.
 *
 * What happens, in order:
 * 1. Another required control in the same scope (the `<nb-step>` the field
 *    sits in, else the whole dialog) is still empty → focus it. "Required and
 *    empty" is read off the DOM Angular already maintains: an `ng-invalid`
 *    input / textarea with an empty value, or an `ng-invalid` `nb-select`.
 *    Next in DOM order first, wrapping around to earlier skipped fields.
 * 2. Otherwise → click the primary button of the nearest action bar: the
 *    last visible, enabled `nbButton` with `status="primary"` /
 *    `status="success"` (or `nbStepperNext`) in the `<nb-step>`, else in the
 *    dialog card's `nb-card-footer`. The button's own handler runs, so
 *    required-field feedback etc. behave exactly like a click. A disabled
 *    button is left alone.
 *
 * Forms with a native submit button are exempt from step 2 (the browser
 * submits on its own); step 1 still applies. The keydown is cancelled
 * whenever the service acts, which also cancels the browser's single-field
 * implicit submission so nothing fires twice.
 *
 * Installed once from `AppComponent`.
 */
@Injectable({ providedIn: 'root' })
export class DialogEnterActionService {
  private installed = false;

  /** Input types whose Enter should act — the HTML implicit-submission set. */
  private static readonly TEXT_INPUT_TYPES = new Set([
    'text',
    'email',
    'number',
    'password',
    'tel',
    'url',
    'date',
    'datetime-local',
    'month',
    'time',
    'week',
  ]);

  private static readonly ACTION_BUTTON =
    'button[nbButton].status-primary, button[nbButton].status-success, button[nbStepperNext]';

  /** Controls that can be a "next required field". */
  private static readonly REQUIRED_CANDIDATES = 'input.ng-invalid, textarea.ng-invalid, nb-select.ng-invalid';

  /** Native submit controls — their presence means the browser submits on Enter. */
  private static readonly NATIVE_SUBMIT =
    'button:not([type]), button[type="submit"], input[type="submit"], input[type="image"]';

  /** Overlays that consume Enter themselves while open. */
  private static readonly ENTER_OWNING_OVERLAY =
    '.cdk-overlay-pane nb-option-list, .cdk-overlay-pane nb-autocomplete, .cdk-overlay-pane nb-datepicker-container';

  install(): void {
    if (this.installed) return;
    this.installed = true;
    document.addEventListener('keydown', (event) => this.onKeydown(event));
  }

  private onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) return;
    if (event.altKey || event.shiftKey) return;

    const field = event.target;
    const withCommand = event.ctrlKey || event.metaKey;
    if (field instanceof HTMLTextAreaElement) {
      if (!withCommand) return; // plain Enter = line break
    } else if (field instanceof HTMLInputElement) {
      if (withCommand || !DialogEnterActionService.TEXT_INPUT_TYPES.has(field.type)) return;
    } else {
      return;
    }
    if (field.hasAttribute('data-enter-ignore') || field.closest('app-search-input')) return;

    const dialog = field.closest('nb-dialog-container');
    if (!dialog) return;
    if (document.querySelector(DialogEnterActionService.ENTER_OWNING_OVERLAY)) return;

    const scope = field.closest('nb-step') ?? dialog;

    const next = this.findNextEmptyRequired(field, scope);
    if (next) {
      event.preventDefault();
      this.focusControl(next);
      return;
    }

    // A form with its own submit button: the browser submits — nothing to add.
    const form = field.form;
    if (form && form.querySelector(DialogEnterActionService.NATIVE_SUBMIT)) return;

    const button = this.findActionButton(scope, dialog);
    if (!button) return;
    event.preventDefault();
    button.click();
  }

  /** First empty required control after `current` in DOM order, wrapping
   *  around to earlier ones the user skipped. `current` itself never counts. */
  private findNextEmptyRequired(current: HTMLElement, scope: Element): HTMLElement | null {
    const candidates = Array.from(
      scope.querySelectorAll<HTMLElement>(DialogEnterActionService.REQUIRED_CANDIDATES)
    ).filter((el) => el !== current && this.isVisible(el) && this.isEmptyRequired(el));
    if (!candidates.length) return null;

    const after = candidates.find(
      (el) => current.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
    );
    return after ?? candidates[0];
  }

  private isEmptyRequired(el: HTMLElement): boolean {
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      if (el.disabled || el.readOnly) return false;
      if (el instanceof HTMLInputElement && !DialogEnterActionService.TEXT_INPUT_TYPES.has(el.type)) {
        return false;
      }
      return el.value.trim() === '';
    }
    // nb-select: ng-invalid on an empty required control — no value to inspect here.
    return !el.classList.contains('disabled') && el.getAttribute('aria-disabled') !== 'true';
  }

  private focusControl(el: HTMLElement): void {
    if (el.tagName.toLowerCase() === 'nb-select') {
      el.querySelector<HTMLElement>('button.select-button')?.focus();
      return;
    }
    el.focus();
  }

  private findActionButton(scope: Element, dialog: Element): HTMLButtonElement | null {
    const footers = dialog.querySelectorAll('nb-card-footer');
    const bar = scope !== dialog ? scope : footers[footers.length - 1];
    if (!bar) return null;

    const candidates = Array.from(
      bar.querySelectorAll<HTMLButtonElement>(DialogEnterActionService.ACTION_BUTTON)
    ).filter((btn) => this.isVisible(btn) && !this.isDisabled(btn));
    return candidates[candidates.length - 1] ?? null;
  }

  private isVisible(el: HTMLElement): boolean {
    return el.offsetParent !== null;
  }

  private isDisabled(btn: HTMLButtonElement): boolean {
    return (
      btn.disabled ||
      btn.getAttribute('aria-disabled') === 'true' ||
      btn.classList.contains('btn-disabled')
    );
  }
}
